/**
 * Dispensar insights financeiros (sync web ↔ mobile via preferencias_ui).
 * Formato: preferencias_ui.insights_dismissed = { [insightId]: ISO8601 }
 * Expira em 7 dias — o insight volta a aparecer se a regra ainda valer.
 */

export const INSIGHTS_DISMISS_TTL_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * @param {object|null|undefined} preferenciasUi
 * @returns {Record<string, string>}
 */
export function getInsightsDismissedMap(preferenciasUi) {
  const raw = preferenciasUi?.insights_dismissed;
  if (!raw || typeof raw !== 'object') return {};
  return pruneExpiredDismissals(raw);
}

/**
 * @param {Record<string, string>|null|undefined} map
 * @param {number} [nowMs]
 * @returns {Record<string, string>}
 */
export function pruneExpiredDismissals(map, nowMs = Date.now()) {
  if (!map || typeof map !== 'object') return {};
  const out = {};
  for (const [id, iso] of Object.entries(map)) {
    if (!id || typeof iso !== 'string') continue;
    const t = Date.parse(iso);
    if (!Number.isFinite(t)) continue;
    if (nowMs - t < INSIGHTS_DISMISS_TTL_MS) {
      out[id] = iso;
    }
  }
  return out;
}

/**
 * @param {Record<string, string>} map
 * @param {string} insightId
 * @param {number} [nowMs]
 */
export function isInsightDismissed(map, insightId, nowMs = Date.now()) {
  if (!insightId || !map) return false;
  const iso = map[insightId];
  if (!iso) return false;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return false;
  return nowMs - t < INSIGHTS_DISMISS_TTL_MS;
}

/**
 * @param {Array<{id?: string}>|null|undefined} insights
 * @param {Record<string, string>} dismissedMap
 */
export function partitionInsights(insights, dismissedMap) {
  const all = Array.isArray(insights) ? insights.filter((i) => i && i.id) : [];
  const visible = [];
  const hidden = [];
  for (const item of all) {
    if (isInsightDismissed(dismissedMap, item.id)) {
      hidden.push(item);
    } else {
      visible.push(item);
    }
  }
  return { visible, hidden, all };
}

/**
 * @param {Record<string, string>} map
 * @param {string} insightId
 * @param {Date} [at]
 */
export function dismissInsightInMap(map, insightId, at = new Date()) {
  const base = pruneExpiredDismissals(map || {});
  if (!insightId) return base;
  return { ...base, [insightId]: at.toISOString() };
}

/**
 * Remove um ou todos os dismissals.
 * @param {Record<string, string>} map
 * @param {string|null} insightId — null restaura todos
 */
export function restoreInsightsInMap(map, insightId = null) {
  const base = pruneExpiredDismissals(map || {});
  if (insightId == null) return {};
  if (!base[insightId]) return base;
  const next = { ...base };
  delete next[insightId];
  return next;
}

/**
 * Restaura apenas ids presentes na lista (ex.: ocultos do mês atual).
 * @param {Record<string, string>} map
 * @param {string[]} insightIds
 */
export function restoreInsightIdsInMap(map, insightIds) {
  const base = pruneExpiredDismissals(map || {});
  if (!insightIds?.length) return base;
  const next = { ...base };
  for (const id of insightIds) {
    delete next[id];
  }
  return next;
}

/**
 * @param {object|null|undefined} preferenciasUiAtual
 * @param {Record<string, string>|null} dismissedMap — null remove a chave
 */
export function buildInsightsDismissedPatch(preferenciasUiAtual, dismissedMap) {
  const base =
    preferenciasUiAtual && typeof preferenciasUiAtual === 'object'
      ? { ...preferenciasUiAtual }
      : {};
  if (dismissedMap == null) {
    base.insights_dismissed = null;
  } else {
    base.insights_dismissed = pruneExpiredDismissals(dismissedMap);
  }
  return base;
}
